import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, Shield } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { siteConfig } from '@/data/mock';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { path: '/', label: 'Startseite' },
    { path: '/ueber-mich', label: 'Über mich' },
    { path: '/leistungen', label: 'Leistungen' },
    { path: '/presse', label: 'Presse' },
    { path: '/partner', label: 'Partner' },
    { path: '/kontakt', label: 'Kontakt' },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-white/95 backdrop-blur-md shadow-md'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-3 group">
            <div className="w-10 h-10 rounded-lg bg-[hsl(213,80%,35%)] flex items-center justify-center shadow-lg group-hover:shadow-xl transition-shadow">
              <Shield className="w-6 h-6 text-white" />
            </div>
            <div>
              <span className="text-xl font-bold text-[hsl(215,25%,15%)] tracking-tight">
                {siteConfig.name.replace('.com', '')}
              </span>
              <span className="text-xl font-bold text-[hsl(213,80%,35%)]">.com</span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-1">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                  isActive(link.path)
                    ? 'text-[hsl(213,80%,35%)] bg-[hsl(213,80%,35%,0.1)]'
                    : 'text-[hsl(215,25%,30%)] hover:text-[hsl(213,80%,35%)] hover:bg-[hsl(213,80%,35%,0.05)]'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          {/* CTA Button */}
          <div className="hidden lg:flex items-center space-x-4">
            <a
              href={`tel:${siteConfig.phone}`}
              className="flex items-center text-sm text-[hsl(215,25%,30%)] hover:text-[hsl(213,80%,35%)] transition-colors"
            >
              <Phone className="w-4 h-4 mr-2" />
              {siteConfig.phone}
            </a>
            <Button asChild className="bg-[hsl(213,80%,35%)] hover:bg-[hsl(213,80%,30%)] text-white shadow-lg hover:shadow-xl transition-all">
              <Link to="/kontakt">Termin vereinbaren</Link>
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2 rounded-lg text-[hsl(215,25%,30%)] hover:bg-[hsl(210,25%,95%)] transition-colors"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="lg:hidden bg-white border-t border-[hsl(210,25%,88%)] shadow-lg">
          <nav className="max-w-7xl mx-auto px-4 py-4 space-y-2">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => setIsMobileMenuOpen(false)}
                className={`block px-4 py-3 rounded-lg text-base font-medium transition-colors ${
                  isActive(link.path)
                    ? 'text-[hsl(213,80%,35%)] bg-[hsl(213,80%,35%,0.1)]'
                    : 'text-[hsl(215,25%,30%)] hover:bg-[hsl(210,25%,95%)]'
                }`}
              >
                {link.label}
              </Link>
            ))}
            <div className="pt-4 border-t border-[hsl(210,25%,88%)]">
              <Button asChild className="w-full bg-[hsl(213,80%,35%)] hover:bg-[hsl(213,80%,30%)] text-white">
                <Link to="/kontakt" onClick={() => setIsMobileMenuOpen(false)}>
                  Termin vereinbaren
                </Link>
              </Button>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;
